// One sound, whatever list it came from (the app's Sound model,
// lib/models/sound.dart). toSound() normalises the rows of GET /sounds,
// /user/<u>/loops, /favorite_loops, /purchases/library and a pack's
// rows into one shape, so every sound row, sheet and the player bar read
// the same fields.
//
// Frozen kit API: soundTitle, packArtLines and soundChip come from
// kit/packs.js, saleTerms and usd from kit/sale.js (tests/w1-kitApi pins
// their names, signatures and outputs).
import { packArtLines, packSound, soundChip, soundTitle } from "./packs.js";
import { saleTerms, startUsd, usd } from "./sale.js";
import { keyName } from "./musicKey.js";

export { packArtLines };

const num = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const str = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);

// The server titles an unnamed sound "Loop <hash8>"; that is not a name.
function serverHashTitle(title, hash) {
  const m = /^loop\s+([0-9a-f]{6,})$/i.exec(String(title || "").trim());
  return !!m && String(hash || "").toLowerCase().startsWith(m[1].toLowerCase());
}

function titleOf(row, md, hash) {
  let name = str(row.title) || str(row.name) || str(row.filename) || str(md.name) || "";
  if (serverHashTitle(name, hash)) name = "";
  return soundTitle({ name, hash });
}

function keyOf(row, md) {
  const k = str(row.key);
  if (k) return k;
  const pc = row.key_pitch_class != null ? row.key_pitch_class : md.key_pitch_class;
  const minor = row.key_is_minor != null ? row.key_is_minor : md.key_is_minor;
  return keyName(pc, minor);
}

function kindOf(row, md) {
  const k = String(row.kind || row.asset_kind || md.asset_kind || "").toLowerCase();
  return /^one[-_ ]?shot$/.test(k) ? "one_shot" : "loop";
}

function tagsOf(row, md) {
  const t = Array.isArray(row.tags) ? row.tags : Array.isArray(md.tags) ? md.tags : [];
  return t.filter((x) => typeof x === "string" && x);
}

// The license state and price, from the row's own fields when the server
// sent them (license_state, price or the flat *_usd fields), else worked
// out from the licence and prices (kit/sale.js saleTerms).
function termsOf(row, md) {
  const licenseType = str(row.license_type) || str(md.license_type) || "CC0";
  const flat = {
    license_type: licenseType,
    min_price_usd: row.min_price_usd != null ? row.min_price_usd : md.min_price_usd,
    suggested_price_usd: row.suggested_price_usd !== undefined ? row.suggested_price_usd : md.suggested_price_usd,
    for_sale: typeof row.for_sale === "boolean" ? row.for_sale : md.for_sale,
  };
  const t = saleTerms(flat);
  const state = str(row.license_state) || t.state;
  const forSale = typeof row.for_sale === "boolean" ? row.for_sale : state === "for_sale";
  const isFree = typeof row.is_free === "boolean" ? row.is_free : state === "open";
  let price = null;
  if (forSale) {
    const p = row.price && typeof row.price === "object" ? row.price : null;
    const minUsd = num(p ? p.min_usd : flat.min_price_usd) ?? t.min ?? 0;
    const suggestedUsd = num(p ? p.suggested_usd : flat.suggested_price_usd) ?? t.suggested ?? null;
    const checkoutMinUsd = num(p ? p.checkout_min_usd : row.checkout_min_usd) ?? t.checkoutMin ?? minUsd;
    price = { minUsd, suggestedUsd, checkoutMinUsd };
  }
  return { licenseType, licenseState: state, forSale, isFree, price };
}

// A chip as SoundLicenseChip draws it: { kind, label }.
function chipFor({ isBonus, isOwned, isFree, forSale, price }, { mine = false } = {}) {
  if (isBonus && !isOwned) return { kind: "bonus", label: "Bonus" };
  if (isOwned && !mine) return { kind: "owned", label: "Owned" };
  if (isFree) return { kind: "free", label: "Free" };
  if (forSale && price) return { kind: "price", label: usd(price.checkoutMinUsd) };
  return { kind: "notForSale", label: "Not for sale" };
}

function packsOf(row) {
  const p = Array.isArray(row.packs) ? row.packs : row.pack && typeof row.pack === "object" ? [row.pack] : [];
  return p.filter((x) => x && x.slug).map((x) => ({ slug: x.slug, title: x.title || x.slug }));
}

/**
 * Normalise one sound row.
 *   me              the viewer's username (their own sounds are owned)
 *   starred         a list or Set of starred hashes
 *   purchasedHashes a list or Set of hashes the viewer bought
 *   packOwned       a pack row whose pack the viewer owns (bought or made)
 */
export function toSound(row, { me = null, starred = null, purchasedHashes = null, packOwned = false } = {}) {
  if (!row || typeof row !== "object") return null;
  const md = row.loop_metadata && typeof row.loop_metadata === "object" ? row.loop_metadata : {};
  const hash = str(row.hash) || str(row.loop_hash) || str(md.hash);
  if (!hash) return null;
  const creator = str(row.creator) || str(row.creator_username) || str(md.original_author) || "";
  const has = (set, h) => !!set && (set instanceof Set ? set.has(h) : Array.isArray(set) && set.includes(h));
  const isPackRow = "loop_hash" in row && ("price_usd" in row || "is_bonus" in row || "position" in row);

  let { licenseType, licenseState, forSale, isFree, price } = termsOf(row, md);
  let isBonus = false;
  const mine = !!me && creator === me;
  const downloadToken = str(row.download_token);
  let isOwned =
    mine ||
    row.is_owned === true ||
    has(purchasedHashes, hash) ||
    // A legacy row (loop_metadata) carries a download token only for a sound
    // you may take: a paid one with a token is yours (kit/myAudio.js).
    (!!md.license_type && licenseType === "rights-reserved" && !!downloadToken) ||
    // A purchased-library row.
    (row.purchased_at != null && row.amount_usd != null);

  if (isPackRow) {
    const a = packSound(row, { packOwned });
    isFree = a.free;
    forSale = a.forSale;
    isBonus = a.bonus;
    isOwned = isOwned || a.owned;
    price = a.price != null ? { minUsd: a.price, suggestedUsd: null, checkoutMinUsd: a.price } : null;
    licenseState = a.free ? "open" : a.forSale ? "for_sale" : "not_for_sale";
  }

  const fields = { isBonus, isOwned, isFree, forSale, price };
  const chip = isPackRow ? soundChip({ ...packSound(row, { packOwned }), owned: isOwned }) : chipFor(fields);
  const ownerChip = chipFor({ ...fields, isOwned: false, isBonus: false }, { mine: true });

  return {
    hash,
    title: titleOf(row, md, hash),
    creator,
    creatorDisplay: str(row.creator_display_name) || null,
    sessionName: str(row.session) || str(row.session_name) || null,
    sessionDisplay: str(row.session_display_name) || null,
    createdAt: str(row.created_at) || str(md.created_at) || null,
    bpm: num(row.bpm) ?? num(md.bpm),
    key: keyOf(row, md),
    kind: kindOf(row, md),
    tags: tagsOf(row, md),
    licenseType,
    licenseState,
    forSale,
    isFree,
    isOwned,
    isBonus,
    mine,
    price,
    chip,
    ownerChip,
    starred: has(starred, hash),
    streamToken: str(row.stream_token),
    downloadToken,
    waveformUrl: str(row.waveform_url) || null,
    shareUrl: str(row.share_url) || null,
    packs: packsOf(row),
    duration: num(row.duration) ?? num(md.duration),
    downloads: num(row.downloads) ?? num(md.downloads) ?? 0,
    favorites: num(row.favorites) ?? num(md.favorites) ?? 0,
    sampleRate: num(row.sample_rate) ?? num(md.sample_rate),
    channels: num(row.channels) ?? num(md.channels),
    size: num(row.size) ?? num(md.size),
    requireAttribution: row.require_attribution === true || md.require_attribution === true,
    allowCommercial: row.allow_commercial !== false && md.allow_commercial !== false,
  };
}

// Free or owned: the full-quality WAV may be taken (downloaded, dragged).
export function canDownload(sound) {
  return !!sound && (sound.isFree || sound.isOwned);
}

// Buyable alone: for sale and not yours yet.
export function canBuy(sound) {
  return !!sound && sound.forSale && !sound.isOwned && !!sound.price;
}

// The one call to action a sound offers (the player bar, the sheet):
//   Download      free or owned
//   Buy · $X      for sale; X is where the buy sheet starts (the suggested
//                 price, at least the minimum)
//   Get it free   for sale with a $0 minimum
//   Not for sale  nothing to take (disabled)
export function soundCta(sound) {
  if (canDownload(sound)) return { label: "Download", kind: "download", disabled: false };
  if (canBuy(sound)) {
    const p = sound.price;
    if (!(p.checkoutMinUsd > 0)) {
      return { label: "Get it free", kind: "buy", disabled: false };
    }
    const at = startUsd({ checkoutMin: p.checkoutMinUsd, suggested: p.suggestedUsd });
    return { label: `Buy · ${usd(at)}`, kind: "buy", disabled: false };
  }
  return { label: "Not for sale", kind: "none", disabled: true };
}

// "D · 120" (key, then whole BPM), leaving out what is unknown.
export function keyBpmText(sound) {
  if (!sound) return "";
  return [sound.key, sound.bpm != null ? String(Math.round(sound.bpm)) : null].filter(Boolean).join(" · ");
}

// "<Title>_<bpm>_<key>.wav" for a dragged or downloaded file; missing
// parts drop out ("Dusty pad_88_E.wav").
export function soundFileName(sound) {
  const parts = [sound && sound.title, sound && sound.bpm != null ? Math.round(sound.bpm) : null, sound && sound.key]
    .filter((p) => p !== null && p !== undefined && String(p).trim())
    .map((p) => String(p).replace(/[\\/:*?"<>|]+/g, "").trim());
  return `${parts.join("_") || "sound"}.wav`;
}
